import { decode as PNGdecode } from 'fast-png';
import { BaseImageFilter, ImageFilterAlias } from 'utils/image-processing';
import colormap from 'colormap';

export interface DepthVisualizationOptions {
    showDepth: boolean;
    showInvalidDepth: boolean;
    opacity: number;
    colorMap: string;
    minDepth: number;
    maxDepth: number;
}

export default class DepthVisualizationFilter extends BaseImageFilter {
    public options: DepthVisualizationOptions;
    private depthData: ArrayBuffer | null = null;
    private colorMap: number[][];

    constructor(options: DepthVisualizationOptions) {
        super();
        this.options = options;
        this.updateColorMap();
    }

    private updateColorMap(): void {
        this.colorMap = colormap({
            colormap: this.options.colorMap,
            nshades: 256,
            format: 'rgba',
            alpha: 1
        });
    }

    private applyColormap(value: number, min: number, max: number): [number, number, number, number] {
        const normalizedValue = (value - min) / (max - min);
        const index = Math.max(Math.min(Math.floor(normalizedValue * 255), 255), 0);
        return this.colorMap[index];
    }

    private colormapDepthData(depthData: Uint16Array, width: number, height: number): Uint8Array {
        const colorData = new Uint8Array(width * height * 4);

        // Calculate min and max depth values
        let minDepth = this.options.minDepth !== undefined ? this.options.minDepth : Number.MAX_VALUE;
        let maxDepth = this.options.maxDepth !== undefined ? this.options.maxDepth : Number.MIN_VALUE;

        // Calculate what the min and max depth values of the current image are
        if (minDepth === maxDepth) {
            for (let i = 0; i < depthData.length; i++) {
                if (depthData[i] < minDepth && depthData[i] !== 0) minDepth = depthData[i];
                if (depthData[i] > maxDepth) maxDepth = depthData[i];
            }
        }

        for (let i = 0; i < depthData.length; i++) {
            let [r, g, b, a] = this.applyColormap(depthData[i], minDepth, maxDepth);
            if (depthData[i] === 0 && !this.options.showInvalidDepth) {
                [r, g, b, a] = [0, 0, 0, 0]; // Fully transparent for invalid depth
            }
            colorData[i * 4] = r;
            colorData[i * 4 + 1] = g;
            colorData[i * 4 + 2] = b;
            colorData[i * 4 + 3] = a * 255; // Convert alpha to 0-255 range
        }

        return colorData;
    }

    private blendImages(rgbData: Uint8ClampedArray, depthColorData: Uint8Array, depthData: Uint16Array, alpha: number): Uint8ClampedArray {
        const blendedData = new Uint8ClampedArray(rgbData.length);

        for (let i = 0; i < rgbData.length; i += 4) {
            const depthIndex = i / 4;
            if (depthData[depthIndex] === 0 && !this.options.showInvalidDepth) {
                // For invalid depth, use original RGB data with full alpha
                blendedData[i] = rgbData[i];
                blendedData[i + 1] = rgbData[i + 1];
                blendedData[i + 2] = rgbData[i + 2];
                blendedData[i + 3] = 255; // Full alpha
            } else {
                // For valid depth, blend as before
                blendedData[i] = Math.floor(rgbData[i] * (1 - alpha) + depthColorData[i] * alpha);
                blendedData[i + 1] = Math.floor(rgbData[i + 1] * (1 - alpha) + depthColorData[i + 1] * alpha);
                blendedData[i + 2] = Math.floor(rgbData[i + 2] * (1 - alpha) + depthColorData[i + 2] * alpha);
                blendedData[i + 3] = 255; // Full alpha
            }
        }

        return blendedData;
    }

    public processImage(src: ImageData, frameNumber: number): ImageData {
        if (!this.options.showDepth || !this.depthData) {
            return src;
        }

        const pngData = PNGdecode(this.depthData);

        // Convert depth data to Uint16Array
        const depthData = new Uint16Array(pngData.data.buffer);

        // Apply colormap to depth data
        const depthColorData = this.colormapDepthData(depthData, src.width, src.height);

        // Blend the images
        const blendedData = this.blendImages(src.data, depthColorData, depthData, this.options.opacity);

        // Create and return the result ImageData
        const result = new ImageData(blendedData, src.width, src.height);

        this.currentProcessedImage = frameNumber;
        return result;
    }

    public configure(options: Partial<DepthVisualizationOptions>): void {
        this.options = { ...this.options, ...options };
        if (options.colorMap) {
            this.updateColorMap();
        }
    }

    public setDepthData(depthData: ArrayBuffer): void {
        this.depthData = depthData;
    }
}

export const createDepthVisualizationFilter = (options: DepthVisualizationOptions) => ({
    modifier: new DepthVisualizationFilter(options),
    alias: ImageFilterAlias.DEPTH_VISUALIZATION,
});
